import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';

import { finalize, take, takeUntil } from 'rxjs';

import { DialogResult, DialogService, FormNavigationService, SpinnerService } from 'dspx-core';

import { API_ENDPOINTS } from '../../constants/api-endpoints.constants';
import { ROUTE_CONSTANTS } from '../../constants/route.constants';
import { TEMPLATES } from '../../constants/templates.constants';
import { POP_UP_CODES, QUESTION_PATHS } from '../../constants/app-data.constants';
import { DASHBOARD_FILTER_NAMES } from '../../constants/grid-data-filter.constants';

import { MaatSorDescriptionGridFilterRequest } from '../../interfaces/maat-sor-description-grid-filter-request.interface';
import { LoggedInUserProfile } from '../../interfaces/logged-in-user-profile.interface';

import { GridDataFilterHandler } from '../../helpers/grid-data-filter-handler';
import { GridDataClientHandler } from '../../helpers/grid-data-client-handler';

import { AppDataService } from '../../services/app-data.service';
import { ServiceWorkerService } from '../../services/service-worker.service';
import { ResponseParserService } from '../../services/response-parser.service';
import { HtmlHelperService } from '../../services/html-helper.service';
import { ErrorService } from '../../services/error.service';
import { FormConfigurationParserService } from '../../services/form-configuration-parser.service';

import { BaseCardSetListComponent } from '../base-card-set-list-component/base-card-set-list.component';

@Component({
  selector: 'app-maat-sor-description-list',
  templateUrl: './maat-sor-description-list.component.html',
  styleUrls: ['./maat-sor-description-list.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class MaatSorDescriptionListComponent extends BaseCardSetListComponent implements OnInit, OnDestroy {

  private loggedInUserProfile: LoggedInUserProfile;
  private filterHandler: GridDataFilterHandler<MaatSorDescriptionGridFilterRequest>;

  private gridDataClientHandler: GridDataClientHandler;

  activeDialog: string = '';

  constructor(
    protected router: Router,
    protected serviceWorkerService: ServiceWorkerService,
    protected appDataService: AppDataService,
    protected spinnerService: SpinnerService,
    protected htmlHelperService: HtmlHelperService,
    protected dialogService: DialogService,
    protected responseParserService: ResponseParserService,
    protected formNavigationService: FormNavigationService,
    private errorService: ErrorService,
    private formConfigurationParserService: FormConfigurationParserService) {
    super(
      router,
      serviceWorkerService,
      appDataService,
      spinnerService,
      htmlHelperService,
      dialogService,
      responseParserService,
      formNavigationService);

    this.initialise(
      TEMPLATES.maatSorDescriptionList,
      () => API_ENDPOINTS.dashboard.getMaatSorDescriptions,
      true);

    this.filterHandler = new GridDataFilterHandler<MaatSorDescriptionGridFilterRequest>(
      this.responseParserService,
      this.appDataService,
      () => this.baseForm.model,
      {
        getLoggedInUserEmail: () => this.loggedInUserProfile?.emailAddress,
        getFilterName: () => DASHBOARD_FILTER_NAMES.maatSorDescriptions,
        searchQuestionPath: [...QUESTION_PATHS.maatSorDescriptionList.search],
        filterQuestionPath: null,
        getFilterRequestFromModels: (searchQuestionModel: any, filterQuestionModel: any) => ({
          searchTerm: searchQuestionModel
        }),
        getDefaultFilterRequest: () => ({})
      });

    this.gridDataClientHandler = new GridDataClientHandler(
      this.appDataService,
      () => this.formConfigurationParserService.findQuestionConfig(this.baseForm.fields, ...QUESTION_PATHS.maatSorDescriptionList.placeholder),
      () => this.loggedInUserProfile?.emailAddress,
      () => DASHBOARD_FILTER_NAMES.maatSorDescriptions);
  }

  ngOnInit(): void {
    this.ignoreCanDeactivateGuard = true;

    this.overrideHtmlTitleSetByDspxFrameworkOnFormNavigation();

    this.waitForServiceWorker().then(waitResult => {

      this.loggedInUserProfile = waitResult.loggedInUserProfile;

      this.filterHandler.setInitialFilterRequest();
    });
  }

  ngOnDestroy(): void {
    this.gridDataClientHandler.unsubscribe();
    this.completeDestroy();
  }

  formLoad(event: any): void {
    this.configData = event.configData;
    this.gridDataClientHandler.copyConfigurationFromStoreIntoGrid()
      .then(() => this.gridDataClientHandler.subscribe())
      .then(() => this.loadMaatSorDescriptionGridDataOnFormLoad(this.filterHandler.getInitialFilterRequest()));
  }

  filterData(event: any): void {
    this.loadMaatSorDescriptionGridData(this.filterHandler.saveFilterRequest(event.action));
  }

  // suppress warning by declaring this method
  actionButton_maatSorDescriptionsPlaceholder_datagrid_doubleClick_Clicked(event: any): void {
  }

  actionButton_maatSorDescriptionsPlaceholder_datagrid_delete_Clicked(event: any): void {
    const maatSorDescription: { id: string, sorCode: string } = event.event.data;
    const { id, sorCode } = maatSorDescription;
    this.dialogService.showConfirmPopup(POP_UP_CODES.maatSorDescriptionList.deleteConfirmation, { sorCode })
      .pipe(take(1))
      .subscribe(result => {
        if (result === DialogResult.ACCEPTED) {
          this.deleteSorDescription(id, sorCode);
        }
      });
  }

  actionButton_maatSorDescriptionsPlaceholder_datagrid_edit_Clicked(event: any) {
    this.router.navigate(['/', ROUTE_CONSTANTS.maatSorDescription.prefix, event.event.data.id]);
  }

  actionButton_maatSorDescriptionsPlaceholder_datagrid_custom_customAdd_Clicked(event: any) {
    this.router.navigate(['/', ROUTE_CONSTANTS.maatSorDescription.prefix]);
  }

  private loadMaatSorDescriptionGridDataOnFormLoad(filterRequest: MaatSorDescriptionGridFilterRequest): void {
    const onGridLoaded = () => this.gridDataClientHandler.copyConfigurationFromStoreIntoGrid();
    this.loadGridDataOnFormLoad(filterRequest, onGridLoaded);
  }

  private loadMaatSorDescriptionGridData(filterRequest: MaatSorDescriptionGridFilterRequest): void {
    const onGridLoaded = () => this.gridDataClientHandler.copyConfigurationFromStoreIntoGrid();
    this.loadGridData(filterRequest, onGridLoaded);
  }

  private deleteSorDescription(id: string, sorCode: string): void {
    this.spinnerService.showSpinner();
    this.appDataService.delete(`${API_ENDPOINTS.maatSorDescription.delete}/${id}`)
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        this.errorService.errorHandler(),
        finalize(() => this.spinnerService.hideSpinner()))
      .subscribe(data => {
        this.dialogService.showPopup(POP_UP_CODES.maatSorDescriptionList.deleteSuccessful, null, { sorCode });
        this.loadMaatSorDescriptionGridData(this.filterHandler.getFilterRequest());
      });
  }
}
